import { ChangeEvent, useContext, useState } from 'react';
import { useSelector } from 'react-redux';

import ConversionUnitForm from '@atom/components/common/conversionUnitsTable/ConversionUnitForm';
import ConversionUnitsTable from '@atom/components/common/conversionUnitsTable/ConversionUnitsTable';
import {
  addConversionUnit,
  removeConversionUnit,
  setConversionUnitActive,
  setDefaultConversionUnit,
  updateConversionUnit,
} from '@atom/components/common/conversionUnitsTable/conversionUnitUtilities';
import CreateEditSchemaModalContext from '@atom/components/common/createEditSchemaModal/CreateEditSchemaModalContext';
import { RadioGroup, Select, TextField } from '@atom/mui';
import { budgetUnitsSelector } from '@atom/selectors/preferencesSelectors';
import { ConversionUnit, SchemaBudgetType } from '@atom/types/schema';

import LabelWithDescription from './LabelWithDescription';

import classes from './createEditSchemaModal.module.css';

const { MenuItem } = Select;
const { RadioButton } = RadioGroup;

const styles = {
  unitOption: {
    textTransform: 'uppercase',
  },
  unitHelperText: {
    whiteSpace: 'nowrap',
  },
};

const CostAndUnitTab = () => {
  const {
    costType,
    setCostType,
    rate,
    setRate,
    unit,
    setUnit,
    conversionUnits,
    setConversionUnits,
  } = useContext(CreateEditSchemaModalContext);

  // 'new' while adding a row, or the row being edited
  const [formTarget, setFormTarget] = useState<'new' | ConversionUnit | null>(
    null,
  );

  const budgetUnits = useSelector(budgetUnitsSelector);
  const unitOptions = ['', ...budgetUnits];

  const updateRate = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setRate(value ? Math.abs(Number(value)) : null);
  };

  // conversion units are relative to the base unit and cost type, so changing
  // either clears them without confirming (AM-19990)
  const clearConversionUnits = () => {
    setConversionUnits([]);
    setFormTarget(null);
  };

  const updateCostType = (value: SchemaBudgetType) => {
    if (value !== costType) {
      clearConversionUnits();
    }

    setCostType(value);
  };

  const updateUnit = (value: string) => {
    if (value !== unit) {
      clearConversionUnits();
    }

    setUnit(value);
  };

  const submitConversionUnit = (
    conversionUnitUnit: string,
    conversionNumber: number,
  ) => {
    setConversionUnits(units =>
      formTarget === 'new'
        ? addConversionUnit(units, conversionUnitUnit, conversionNumber)
        : updateConversionUnit(units, formTarget.id, {
            unit: conversionUnitUnit,
            conversionNumber,
          }),
    );
    setFormTarget(null);
  };

  const deleteConversionUnit = (id: string) => {
    setConversionUnits(units => removeConversionUnit(units, id));

    if (formTarget !== 'new' && formTarget?.id === id) {
      setFormTarget(null);
    }
  };

  const showUnit = costType === SchemaBudgetType.VARIABLE_COST;
  const showCostAndUnit = costType === SchemaBudgetType.FIXED;

  const showConversionUnits = [
    SchemaBudgetType.VARIABLE_COST,
    SchemaBudgetType.FIXED,
  ].includes(costType);
  const helperText = 'Base unit to track usage and stock.';
  const helperTextSx = styles.unitHelperText;

  return (
    <div className={classes.tabContainer}>
      <div className={classes.tabTitle}>Cost &amp; Unit</div>
      <RadioGroup
        value={costType}
        onChange={(_, value) => updateCostType(value as SchemaBudgetType)}
        sx={{ gap: '1rem' }}
      >
        <RadioButton
          value={SchemaBudgetType.VARIABLE}
          label={
            <LabelWithDescription
              title="Variable"
              description="Set the cost and unit at the individual inventory item level."
            />
          }
        />
        <RadioButton
          value={SchemaBudgetType.VARIABLE_COST}
          label={
            <LabelWithDescription
              title="Variable Cost"
              description="Set a fixed unit for all inventory of this type."
            />
          }
        />
        {showUnit && (
          <div className={classes.unitRow}>
            <Select
              label="Unit"
              helperText={helperText}
              helperTextSx={helperTextSx}
              fullWidth
              InputProps={{ style: styles.unitOption }}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                updateUnit(event.target.value)
              }
              value={unit}
            >
              {unitOptions.map(option => (
                <MenuItem
                  key={option}
                  value={option || null}
                  style={styles.unitOption}
                >
                  {option}
                </MenuItem>
              ))}
            </Select>
          </div>
        )}
        <RadioButton
          value={SchemaBudgetType.FIXED}
          label={
            <LabelWithDescription
              title="Fixed"
              description="Set a fixed cost and unit for all inventory of this type."
            />
          }
        />
        {showCostAndUnit && (
          <div className={classes.costUnitRow}>
            <TextField
              label="Cost"
              value={rate ?? ''}
              type="number"
              fullWidth
              onChange={updateRate}
              slotProps={{ input: { startAdornment: '$' } }}
            />
            <div className={classes.perSeparator}>per</div>
            <Select
              label="Unit"
              helperText={helperText}
              helperTextSx={helperTextSx}
              fullWidth
              InputProps={{ style: styles.unitOption }}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                updateUnit(event.target.value)
              }
              value={unit}
            >
              {unitOptions.map(option => (
                <MenuItem
                  key={option}
                  value={option || null}
                  style={styles.unitOption}
                >
                  {option}
                </MenuItem>
              ))}
            </Select>
          </div>
        )}
      </RadioGroup>
      {/* outside the RadioGroup, or its Default radios change the cost type */}
      {showConversionUnits && (
        <div className={classes.conversionUnitsRow}>
          <div className={classes.tabTitle}>Conversion Units</div>
          <ConversionUnitsTable
            conversionUnits={conversionUnits}
            baseUnit={unit}
            onDefaultChange={id =>
              setConversionUnits(units => setDefaultConversionUnit(units, id))
            }
            onActiveChange={(id, active) =>
              setConversionUnits(units =>
                setConversionUnitActive(units, id, active),
              )
            }
            onEdit={setFormTarget}
            onDelete={deleteConversionUnit}
            onAdd={() => setFormTarget('new')}
            addDisabled={!unit}
            renderForm={
              formTarget !== null &&
              (columnCount => (
                <ConversionUnitForm
                  key={formTarget === 'new' ? 'new' : formTarget.id}
                  conversionUnits={conversionUnits}
                  unitOptions={budgetUnits}
                  baseUnit={unit}
                  columnCount={columnCount}
                  conversionUnit={formTarget === 'new' ? undefined : formTarget}
                  onSubmit={submitConversionUnit}
                  onCancel={() => setFormTarget(null)}
                  dataCy="createEditSchemaConversionUnitForm"
                />
              ))
            }
            dataCy="createEditSchemaConversionUnitsTable"
          />
        </div>
      )}
    </div>
  );
};

export default CostAndUnitTab;
